import {Form} from "react-router-dom";
import React, {useMemo, useState} from "react";
import {toast} from "react-toastify";
import AddDivisionMutation from "./mutations/AddDivision";
import {SPORTS} from "../../constants/vars";
import {GlobalStyles} from "../../constants/styles";
import useHasFeature from "../common/hooks/useHasFeature";
import useThemeColors from "../organization/portal/useThemeColors";
import {alphaFromHex} from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";
import {AGE_GROUP_OPTIONS, COMPETITION_LEVEL_OPTIONS} from "../../constants/divisionAssignmentOptions";

export default function AddDivisionModal({
                                             show,
                                             onClose,
                                             onSubmit,
                                             divisions,
                                             setDivisions,
                                             orgSlug,
                                             waivers,
                                         }) {
    const colors = useThemeColors();
    const {t} = useTranslation();
    const [addDivisionMutation] = AddDivisionMutation();
    const [selectedSport, setSelectedSport] = useState("");
    const [selectedWaiverIds, setSelectedWaiverIds] = useState([]);

    const {hasFeature} = useHasFeature();
    const canUseWaiver = hasFeature("waiver_management");

    const modalStyles = useMemo(() => {
        return {
            wrap: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {display: "flex", alignItems: "center", gap: 10},
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                padding: "6px 10px",
                borderRadius: 999,
                color: colors.textOnPrimary,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
            },
            title: {margin: 0, fontSize: 16, fontWeight: 900, color: "#14151a"},
            closeBtn: {
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            body: {padding: 14, background: "#fff"},
            sectionTitle: {
                margin: "2px 0 10px",
                fontSize: 13,
                fontWeight: 900,
                color: "rgba(20,21,26,0.78)",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            card: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                marginBottom: 12,
            },
            label: {
                fontSize: 12.5,
                fontWeight: 800,
                color: "rgba(20,21,26,0.70)",
                marginBottom: 6,
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "10px 12px",
                fontWeight: 700,
                outline: "none",
            },
            select: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "0px 12px",
                fontWeight: 800,
                outline: "none",
                backgroundColor: "#fff",
            },
            hint: {
                marginTop: 6,
                color: "rgba(20,21,26,0.55)",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.25,
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 14px",
                background: "rgba(0,0,0,0.02)",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
            },
            btnGhost: {
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.12)",
                color: "#14151a",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const selectClass = (value) =>
        `form-control kbola-select ${value ? "kbola-select--filled" : "kbola-select--placeholder"}`;
    const selectedWaiverTitles = useMemo(() => {
        const selectedSet = new Set((selectedWaiverIds || []).map((id) => String(id)));
        return (waivers || [])
            .filter((waiver) => selectedSet.has(String(waiver.id)))
            .map((waiver) => waiver.title);
    }, [selectedWaiverIds, waivers]);

    if (!show) return null;

    return (
        <>
            <div
                className="modal fade show d-block"
                id="add-division-modal"
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
            >
                <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
                    <div className="modal-content" style={modalStyles.wrap}>
                        <Form
                            method="post"
                            onSubmit={(e) => {
                                e.preventDefault();
                                const form = e.currentTarget;
                                const fieldValue = (name) => form.elements.namedItem(name)?.value;

                                addDivisionMutation({
                                    variables: {
                                        name: fieldValue("name"),
                                        sport: fieldValue("sport"),
                                        competitionLevel: fieldValue("competition_level"),
                                        ageGroup: fieldValue("age_group"),
                                        mountDistance: parseFloat(fieldValue("mount_distance")) || 0,
                                        pitchLimit: parseInt(fieldValue("pitch_limit")) || 0,
                                        restingGames: parseInt(fieldValue("resting_games")) || 0,
                                        orgSlug: orgSlug,
                                        waiverIds: selectedWaiverIds,
                                        gameDuration: fieldValue("game_duration") || 0,
                                    },
                                })
                                    .then((res) => {
                                        if (res.data?.createDivision?.division) {
                                            toast.success(t("addDivision.createdSuccess"), {
                                                autoClose: 3000,
                                                toastId: "division_added",
                                                position: "top-center",
                                                closeOnClick: true,
                                            });

                                            setDivisions([...divisions, res.data.createDivision.division]);
                                            e.target.reset();
                                            setSelectedSport("");
                                            setSelectedWaiverIds([]);
                                            onClose();
                                            onSubmit?.();
                                        }
                                    })
                                    .catch((err) => {
                                        toast.error(getLocalizedGraphQLError(err, t, "addDivision.error"), {
                                            autoClose: 5000,
                                            toastId: "division_error",
                                            position: "top-center",
                                            closeOnClick: true,
                                        });
                                    });
                            }}
                        >
                            <div className="modal-header" style={modalStyles.header}>
                                <div style={modalStyles.titleWrap}>
                                    <span style={modalStyles.badge}>{t("addDivision.newBadge")}</span>
                                    <h4 className="modal-title" style={modalStyles.title}>
                                        {t("addDivision.title")}
                                    </h4>
                                </div>

                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label={t("addDivision.close")}
                                    style={modalStyles.closeBtn}
                                    title={t("addDivision.close")}
                                >
                                    <span aria-hidden="true" style={{fontSize: 20, lineHeight: 1}}>
                                        &times;
                                    </span>
                                </button>
                            </div>

                            <div className="modal-body" style={modalStyles.body}>
                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>{t("addDivision.basics")}</div>

                                    <div className="form-group" style={{marginBottom: 12}}>
                                        <label style={modalStyles.label}>{t("addDivision.name")}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            required
                                            name="name"
                                            placeholder={t("addDivision.namePlaceholder")}
                                            style={modalStyles.input}
                                        />
                                    </div>

                                    <div className="form-group" style={{marginBottom: 12}}>
                                        <label style={modalStyles.label}>{t("addDivision.competitionLevel")}</label>
                                        <select className="form-control" name="competition_level" style={modalStyles.select} defaultValue="">
                                            <option value="">{t("addDivision.optionalSelection")}</option>
                                            {COMPETITION_LEVEL_OPTIONS.map((option) => <option key={option.value} value={option.value}>{t(`addDivision.competitionOptions.${option.value}`, option.label)}</option>)}
                                        </select>
                                    </div>

                                    <div className="form-group" style={{marginBottom: 12}}>
                                        <label style={modalStyles.label}>{t("addDivision.ageGroup")}</label>
                                        <select className="form-control" name="age_group" style={modalStyles.select} defaultValue="">
                                            <option value="">{t("addDivision.optionalSelection")}</option>
                                            {AGE_GROUP_OPTIONS.map((option) => <option key={option.value} value={option.value}>{t(`addDivision.ageOptions.${option.value}`, option.label)}</option>)}
                                        </select>
                                    </div>

                                    <div className="form-group" style={{marginBottom: 0}}>
                                        <label style={modalStyles.label}>{t("addDivision.sport")}</label>
                                        <select
                                            className={selectClass(selectedSport)}
                                            name="sport"
                                            required
                                            onChange={(e) => setSelectedSport(e.target.value)}
                                            style={modalStyles.select}
                                        >
                                            <option value="">{t("addDivision.chooseSport")}</option>
                                            {SPORTS.map((sport) => (
                                                <option key={sport.key} value={sport.key}>
                                                    {sport.value}
                                                </option>
                                            ))}
                                        </select>
                                        <div style={modalStyles.hint}>
                                            {t("addDivision.sportHint")}
                                        </div>
                                    </div>
                                </div>

                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>{t("addDivision.rules")}</div>

                                    {["baseball", "softball"].includes(selectedSport) && (
                                        <>
                                            <div className="form-group" style={{marginBottom: 12}}>
                                                <label style={modalStyles.label}>
                                                    {t("addDivision.mountDistance")}
                                                </label>
                                                <input
                                                    type="number"
                                                    step="any"
                                                    inputMode="decimal"
                                                    className="form-control"
                                                    name="mount_distance"
                                                    placeholder={t("addDivision.mountDistancePlaceholder")}
                                                    required
                                                    style={modalStyles.input}
                                                />
                                                <div style={modalStyles.hint}>
                                                    {t("addDivision.mountDistanceHint")}
                                                </div>
                                            </div>

                                            <div className="form-group" style={{marginBottom: 12}}>
                                                <label style={modalStyles.label}>
                                                    {t("addDivision.pitchLimit")}
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="pitch_limit"
                                                    placeholder={t("addDivision.pitchLimitPlaceholder")}
                                                    required
                                                    style={modalStyles.input}
                                                />
                                            </div>

                                            <div className="form-group" style={{marginBottom: 12}}>
                                                <label style={modalStyles.label}>
                                                    {t("addDivision.restingGames")}
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="resting_games"
                                                    placeholder={t("addDivision.restingGamesPlaceholder")}
                                                    required
                                                    style={modalStyles.input}
                                                />
                                                <div style={modalStyles.hint}>
                                                    {t("addDivision.restingGamesHint")}
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    <div className="form-group" style={{marginBottom: 0}}>
                                        <label style={modalStyles.label}>
                                            {t("addDivision.gameDuration")}
                                        </label>
                                        <input
                                            type="number"
                                            step="any"
                                            className="form-control"
                                            name="game_duration"
                                            placeholder={t("addDivision.gameDurationPlaceholder")}
                                            required
                                            style={modalStyles.input}
                                        />
                                    </div>
                                </div>

                                {canUseWaiver && (
                                    <div style={modalStyles.card}>
                                        <div style={modalStyles.sectionTitle}>{t("addDivision.policy")}</div>

                                        <div className="form-group" style={{marginBottom: 0}}>
                                            <label style={modalStyles.label}>
                                                {t("addDivision.waiverOptional")}
                                            </label>
                                            <div className="kbola-waiver-picker-wrap">
                                                <select
                                                    className={`${selectClass(selectedWaiverIds.length)} kbola-waiver-picker`}
                                                    name="waiverIds"
                                                    style={modalStyles.select}
                                                    multiple
                                                    value={selectedWaiverIds}
                                                    onChange={(e) =>
                                                        setSelectedWaiverIds(
                                                            Array.from(e.target.selectedOptions).map((opt) => opt.value)
                                                        )
                                                    }
                                                    size={Math.min(6, Math.max(3, waivers?.length || 0))}
                                                >
                                                    {waivers?.map((waiver) => (
                                                        <option key={waiver.id} value={waiver.id}>
                                                            {waiver.title}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            {selectedWaiverTitles.length > 0 && (
                                                <div className="kbola-waiver-chip-row">
                                                    {selectedWaiverTitles.map((title, index) => (
                                                        <span className="kbola-waiver-chip" key={`${title}-${index}`}>
                                                            {title}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            <div style={modalStyles.hint}>
                                                {t("addDivision.waiverHint")}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer" style={modalStyles.footer}>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={onClose}
                                    style={{...modalStyles.btn, ...modalStyles.btnGhost}}
                                >
                                    <i className="fas fa-times"/>
                                    {t("addDivision.close")}
                                </button>

                                <button
                                    type="submit"
                                    className="btn"
                                    style={{...modalStyles.btn, ...modalStyles.btnPrimary}}
                                >
                                    <i className="fas fa-check"/>
                                    {t("addDivision.save")}
                                </button>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>

            <style>{`
                #add-division-modal.modal {
                  background: rgba(0, 0, 0, 0.45);
                  backdrop-filter: blur(2px);
                }

                #add-division-modal .modal-dialog {
                  max-height: 92vh;
                  display: flex;
                  flex-direction: column;
                  margin-top: 1.25rem;
                  margin-bottom: 1.25rem;
                }

                #add-division-modal .modal-content {
                  flex: 1;
                  overflow: hidden;
                  display: flex;
                  flex-direction: column;
                }

                #add-division-modal .modal-body {
                  overflow-y: auto;
                  max-height: 66vh;
                }

                #add-division-modal .form-control:focus {
                  border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.9);
                  box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                }

                #add-division-modal .kbola-select--placeholder {
                  color: rgba(20, 21, 26, 0.55) !important;
                }

                #add-division-modal .kbola-select--filled {
                  color: rgba(20, 21, 26, 0.92) !important;
                }

                #add-division-modal .kbola-waiver-picker-wrap {
                  border-radius: 12px;
                  border: 1px solid rgba(0, 0, 0, 0.11);
                  background: linear-gradient(180deg, rgba(255,255,255,0.94), rgba(247,248,250,0.92));
                  padding: 8px;
                }

                #add-division-modal .kbola-waiver-picker {
                  min-height: 132px;
                  height: auto !important;
                  border: none !important;
                  background: transparent !important;
                  box-shadow: none !important;
                  padding: 0 !important;
                }

                #add-division-modal .kbola-waiver-picker option {
                  border-radius: 9px;
                  margin: 2px 0;
                  padding: 9px 10px;
                  font-weight: 700;
                }

                #add-division-modal .kbola-waiver-picker option:checked {
                  background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.72);
                  color: #14151a;
                }

                #add-division-modal .kbola-waiver-picker-wrap:focus-within {
                  border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.95);
                  box-shadow: 0 0 0 0.22rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.22);
                }

                #add-division-modal .kbola-waiver-chip-row {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 6px;
                  margin-top: 8px;
                }

                #add-division-modal .kbola-waiver-chip {
                  font-size: 11px;
                  font-weight: 800;
                  border-radius: 999px;
                  padding: 5px 9px;
                  color: rgba(20, 21, 26, 0.9);
                  border: 1px solid rgba(0, 0, 0, 0.1);
                  background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.42);
                  max-width: 100%;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }

                #add-division-modal .kbola-waiver-picker::-webkit-scrollbar {
                  width: 10px;
                }

                #add-division-modal .kbola-waiver-picker::-webkit-scrollbar-thumb {
                  border-radius: 999px;
                  border: 2px solid rgba(255,255,255,0.95);
                  background: rgba(20, 21, 26, 0.24);
                }

                @media (max-width: 768px) {
                  #add-division-modal .modal-dialog {
                    max-width: 95%;
                    margin: 0.9rem auto;
                  }
                  #add-division-modal .modal-body {
                    max-height: 70vh;
                  }
                }
            `}</style>
        </>
    );
}
